import React, { useEffect, useMemo, useRef } from "react";
import {
	Channel,
	ChannelList,
	ChannelListItemUIProps,
	Chat,
	MessageComposer,
	MessageList,
	Thread,
	useChatContext,
	Window,
	WithComponents,
} from "stream-chat-react";

import { AscDesc } from "stream-chat";
import "stream-chat-react/dist/css/index.css";
import styled from "styled-components";
import backgroundImage from "@assets/images/messagerie/background2.png";
import { Environment } from "@core/config/environment";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useConnectUser } from "@utils/messaging/hooks";
import { ChannelListPreview } from "@ui/components/messagingComponents/channel/channelListPreview";
import { ChannelHeaderCustom } from "@ui/components/messagingComponents/channelHeader/channelHeaderCustom";
import { EmptyChannel } from "@ui/components/messagingComponents/emptyChannel";
import { Page } from "@ui/components/page";

interface ChatMessageProps extends React.HTMLAttributes<HTMLDivElement> {
	profileId: string;
	channel?: string | null;
}

const Chatmessage: React.FC<ChatMessageProps> = ({ profileId, channel }) => {
	const currentProfile = useCurrentProfile();

	const connectUser = useConnectUser({
		profileId,
		userToken: currentProfile?.chatToken?.token,
	});

	if (!connectUser) {
		return null;
	}

	if (connectUser.loading) {
		return <div>Loading...</div>;
	}

	return (
		<Container>
			<Title>Ma Messagerie</Title>
			<Chat client={connectUser.chatClient}>
				<ChatContent profileId={profileId} channel={channel} />
			</Chat>
		</Container>
	);
};

const ChatContent: React.FC<ChatMessageProps> = ({ profileId, channel }) => {
	const filters = { type: "messaging", members: { $in: [profileId] } };
	const sort: { last_message_at: AscDesc } = { last_message_at: -1 };

	const channelRef = useRef<HTMLDivElement>(null);
	const { channel: activeChannel } = useChatContext();
	const overrides = useMemo(
		() => ({
			ChannelListItemUI: (props: ChannelListItemUIProps) => <ChannelListPreview {...props} isChatMessage={true} />,
		}),
		[]
	);

	useEffect(() => {
		channelRef.current?.scrollIntoView({ behavior: "smooth" });
		console.log("open channel");
	}, [activeChannel]);

	return (
		<ContentContainer>
			<AbsoluteWrapper>
				<ChannelListContainer>
					<WithComponents overrides={overrides}>
						<ChannelList
							EmptyStateIndicator={EmptyChannel}
							showChannelSearch
							filters={filters}
							sort={sort}
							customActiveChannel={channel ?? undefined}
						/>
					</WithComponents>
				</ChannelListContainer>
				<ChannelContainer ref={channelRef}>
					<Channel>
						<Window>
							<ChannelHeaderCustom />
							<MessageList />
							<MessageComposer />
						</Window>
						<Thread />
					</Channel>
				</ChannelContainer>
			</AbsoluteWrapper>
		</ContentContainer>
	);
};

const Container = styled(Page)`
	display: flex;
	flex-direction: column;
	height: 100%;
`;

const ContentContainer = styled.div`
	border-radius: 10px;
	flex: 1;
	position: relative;
`;

const AbsoluteWrapper = styled.div`
	display: flex;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;

	@media (max-width: 768px) {
		overflow-x: scroll;

		scroll-snap-type: x mandatory;
		scroll-behavior: smooth;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: none;

		& > * {
			scroll-snap-align: start;
			width: 100%;
			flex-shrink: 0;
			box-sizing: border-box;
		}
	}
`;

const Title = styled.h1`
	font-size: 24px;
	font-weight: bold;
	margin-bottom: 10px;
	color: #333;
`;

const ChannelListContainer = styled.div`
	overflow-y: auto;

	.str-chat__channel-list {
		border-radius: 10px;
	}

	.str-chat__search {
		border-radius: 10px 0px 0px 0px;
	}

	.str-chat {
		border-radius: 10px 0px 0px 10px;

		--str-chat__accent-primary: #009688;
	}

	.str-chat__avatar {
		background-color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "#bf360c")};
		color: white;

		&::after {
			content: none;
		}
	}
`;

const ChannelContainer = styled.div`
	flex-grow: 1;
	overflow-y: auto;

	.str-chat {
		--str-chat__accent-primary: ${({ theme }) => theme.colors.primary};
		--str-chat__chat-bg-outgoing: ${({ theme }) =>
			Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "#3da8f8"};
		--str-chat__chat-text-outgoing: white;
		--str-chat__chat-bg-incoming: #aeaeae;
	}

	.str-chat__avatar {
		background-color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "#bf360c")};
		color: white;

		&::after {
			content: none;
		}
	}

	.str-chat__list {
		background-image: url(${backgroundImage});
		background-repeat: no-repeat;
		background-size: cover;
		border-radius: 0px;
	}

	.str-chat__message-composer {
		border-radius: 0px;
	}

	.str-chat__message-composer-compose-area {
		background-color: #ffff;
	}

	.str-chat__send-button:disabled {
		background-color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "#3da8f8")};
	}

	border-radius: 0px 10px 10px 0px;
`;

export default Chatmessage;
